import {
    AdjustmentsHorizontalIcon,
    BookOpenIcon,
    BuildingOffice2Icon,
    FingerPrintIcon,
    GlobeAltIcon,
    InformationCircleIcon,
    MagnifyingGlassIcon,
    UserIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import {
    ActionIcon,
    Box,
    Button,
    Group,
    rem,
    Select,
    Stack,
    Text,
    TextInput,
    Tooltip,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { SVGProps, useEffect, useRef, useState } from "react";
import { FilterPill } from "../../../components/common/FilterPill";
import { useSearchConfig } from "../../../context/SearchConfigContext";
import { LanguageProficiencyService } from "../../../services/languageProficiencyService";
import { LanguageService } from "../../../services/languageService";
import { OfficeLocationService } from "../../../services/officeLocationService";

const IconMagnifyingGlass = (props: SVGProps<SVGSVGElement>) => (
    <MagnifyingGlassIcon {...props} />
);
const IconXMark = (props: SVGProps<SVGSVGElement>) => <XMarkIcon {...props} />;
const IconInformationCircle = (props: SVGProps<SVGSVGElement>) => (
    <InformationCircleIcon {...props} />
);
const IconAdjustmentsHorizontal = (props: SVGProps<SVGSVGElement>) => (
    <AdjustmentsHorizontalIcon {...props} />
);
const IconBuilding = (props: SVGProps<SVGSVGElement>) => (
    <BuildingOffice2Icon {...props} />
);
const IconGlobe = (props: SVGProps<SVGSVGElement>) => <GlobeAltIcon {...props} />;
const IconUser = (props: SVGProps<SVGSVGElement>) => <UserIcon {...props} />;
const IconFingerPrint = (props: SVGProps<SVGSVGElement>) => <FingerPrintIcon {...props} />;
const IconCheckCircle = (props: SVGProps<SVGSVGElement>) => (
    <CheckCircleIcon {...props} />
);

interface CVListSearchProps {
    searchQuery: string;
    onSearchChange: (value: string) => void;
    onSearchClear: () => void;
    onSearchSubmit: (e: React.FormEvent) => void;
    availabilityFilterActive: boolean;
    onAvailabilityFilterChange: () => void;
    availabilityDays: number | null;
    onAvailabilityDaysChange: (days: number | null) => void;
    inactiveFilterActive: boolean;
    onInactiveFilterChange: () => void;
    selectedLocation: string | null;
    onLocationChange: (value: string | null) => void;
    selectedLanguage: number | null;
    onLanguageChange: (value: number | null) => void;
    selectedProficiency: number | null;
    onProficiencyChange: (value: number | null) => void;
    isFiltered: boolean;
    loading: boolean;
}

export function CVListSearch({
    searchQuery,
    onSearchChange,
    onSearchClear,
    onSearchSubmit,
    availabilityFilterActive,
    onAvailabilityFilterChange,
    availabilityDays,
    onAvailabilityDaysChange,
    inactiveFilterActive,
    onInactiveFilterChange,
    selectedLocation,
    onLocationChange,
    selectedLanguage,
    onLanguageChange,
    selectedProficiency,
    onProficiencyChange,
    isFiltered,
    loading,
}: CVListSearchProps) {
    const searchInputRef = useRef<HTMLInputElement>(null);
    const officeLocationService = new OfficeLocationService();
    const languageService = new LanguageService();
    const proficiencyService = new LanguageProficiencyService();
    const { config, updateConfig, openModal } = useSearchConfig();

    // Local input state — keeps typing instant and only propagates to parent after debounce
    const [inputValue, setInputValue] = useState(searchQuery);
    const [debouncedInput] = useDebouncedValue(inputValue, config.debounceDelay);

    // Propagate debounced value up (triggers search + page reset in parent)
    useEffect(() => {
        onSearchChange(debouncedInput);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedInput]);

    // Sync when parent clears externally (e.g. Escape from parent, filter clear)
    useEffect(() => {
        if (searchQuery === "") setInputValue("");
    }, [searchQuery]);

    const handleLocalClear = () => {
        setInputValue("");
        onSearchClear();
    };

    const [languages, setLanguages] = useState<{ value: string; label: string }[]>([]);
    const [proficiencies, setProficiencies] = useState<{ value: string; label: string }[]>([]);
    const [locationOptions, setLocationOptions] = useState<{ value: string; label: string }[]>([]);

    useEffect(() => {
        const fetchLocations = async () => {
            try {
                const locations = await officeLocationService.getOfficeLocationsDictionary();
                setLocationOptions(
                    locations.map((loc) => ({
                        value: loc.id.toString(),
                        label: loc.name,
                    }))
                );
            } catch (error) {
                console.error("Error fetching office locations:", error);
            }
        };

        fetchLocations();
    }, []);

    useEffect(() => {
        const fetchLanguages = async () => {
            try {
                const langs = await languageService.getLanguages();
                setLanguages(
                    langs.map((lang) => ({
                        value: lang.id.toString(),
                        label: lang.englishName,
                    }))
                );
            } catch (error) {
                console.error("Error fetching languages:", error);
            }
        };

        const fetchProficiencies = async () => {
            try {
                const profs = await proficiencyService.getLanguageProficiencies();
                setProficiencies(
                    profs.map((prof) => ({
                        value: prof.id.toString(),
                        label: prof.level,
                    }))
                );
            } catch (error) {
                console.error("Error fetching proficiencies:", error);
            }
        };

        fetchLanguages();
        fetchProficiencies();
    }, []);

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Escape") {
            if (inputValue.trim()) {
                handleLocalClear();
            } else {
                e.currentTarget.blur();
            }
        }
    };

    const selectedLocationLabel =
        locationOptions.find((o) => o.value === selectedLocation)?.label ?? null;
    const selectedLanguageLabel =
        languages.find((l) => l.value === selectedLanguage?.toString())?.label ?? null;
    const selectedProficiencyLabel =
        proficiencies.find((p) => p.value === selectedProficiency?.toString())?.label ?? null;

    return (
        <Stack gap="xs" mb="sm">
            {/* ── Row 1: search bar + config ── */}
            <Group align="center" gap="sm" wrap="nowrap">
                <Box style={{ flex: 1 }}>
                    <form onSubmit={onSearchSubmit} role="search" aria-label="Search CVs">
                        <TextInput
                            ref={searchInputRef}
                            size="md"
                            placeholder={
                                isFiltered
                                    ? "Search disabled while using filtered results"
                                    : "Find the perfect candidate (e.g. Java, React, TypeScript…)"
                            }
                            value={inputValue}
                            onChange={(e) => setInputValue(e.currentTarget.value)}
                            onKeyDown={handleSearchKeyDown}
                            leftSection={<IconMagnifyingGlass width={20} />}
                            disabled={isFiltered}
                            aria-label="Search by first name, last name, technology, or certificate"
                            aria-describedby="search-help-text"
                            rightSection={
                                <Group gap={4} align="center" wrap="nowrap" pr={4}>
                                    {inputValue && (
                                        <ActionIcon
                                            onClick={handleLocalClear}
                                            variant="subtle"
                                            color="gray"
                                            size="sm"
                                            tabIndex={0}
                                            aria-label="Clear search"
                                        >
                                            <IconXMark width={16} />
                                        </ActionIcon>
                                    )}
                                    <Tooltip
                                        label={
                                            <Box>
                                                <Text size="sm" fw={500} mb="xs">
                                                    You can search for:
                                                </Text>
                                                <Text size="xs">• First name (e.g., John)</Text>
                                                <Text size="xs">• Last name (e.g., Smith)</Text>
                                                <Text size="xs">• Technology (e.g., Java, React)</Text>
                                                <Text size="xs">• Certificate (e.g., AWS, Azure)</Text>
                                                <Text size="xs" mt="xs" c="dimmed">
                                                    Tip: Use spaces or commas to separate keywords.
                                                    Wrap multiple words in "quotes" to search as one phrase (e.g., "azure storage").
                                                </Text>
                                            </Box>
                                        }
                                        position="bottom-end"
                                        withArrow
                                        multiline
                                        w={280}
                                    >
                                        <ActionIcon
                                            variant="subtle"
                                            color="gray"
                                            size="sm"
                                            style={{ cursor: "help" }}
                                            tabIndex={0}
                                            aria-label="Search help"
                                        >
                                            <IconInformationCircle width={16} />
                                        </ActionIcon>
                                    </Tooltip>
                                </Group>
                            }
                        />
                        <div id="search-help-text" style={{ display: "none" }}>
                            Search for first names, last names, technologies or certificates.
                            Use spaces or commas to separate multiple keywords. Press Escape to clear.
                        </div>
                    </form>
                </Box>

                <Tooltip label="Configure search settings">
                    <Button
                        variant="subtle"
                        leftSection={<IconAdjustmentsHorizontal width={16} height={16} />}
                        onClick={openModal}
                        size="sm"
                        disabled={isFiltered}
                    >
                        Search Configuration
                    </Button>
                </Tooltip>

                <Tooltip label="Read more in FAQ" position="bottom" withArrow>
                    <ActionIcon
                        component="a"
                        href="/faq/search"
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="subtle"
                        color="brand"
                        size="lg"
                        aria-label="Read more in FAQ"
                    >
                        <BookOpenIcon style={{ width: 18, height: 18 }} />
                    </ActionIcon>
                </Tooltip>
            </Group>

            {/* ── Row 2: filter pills ── */}
            <Group gap="xs" align="flex-start" wrap="wrap">

                {/* Available/Bench */}
                <FilterPill
                    icon={
                        <IconCheckCircle
                            style={{
                                width: rem(14),
                                height: rem(14),
                                color: availabilityFilterActive || availabilityDays
                                    ? "var(--mantine-color-brand-5)"
                                    : "var(--mantine-color-dimmed)",
                            }}
                        />
                    }
                    label="Bench"
                    disabled={isFiltered}
                    tags={[
                        ...(availabilityFilterActive
                            ? [{ label: "Available only", onRemove: onAvailabilityFilterChange }]
                            : []),
                        ...(availabilityDays
                            ? [{ label: `Within ${availabilityDays}d`, onRemove: () => onAvailabilityDaysChange(null) }]
                            : []),
                    ]}
                >
                    <Box pt={6}>
                        <Button
                            fullWidth
                            size="compact-xs"
                            variant={availabilityFilterActive ? "filled" : "light"}
                            color="brand"
                            onClick={onAvailabilityFilterChange}
                            disabled={isFiltered}
                            aria-pressed={availabilityFilterActive}
                        >
                            {availabilityFilterActive ? "✓ Show Available only" : "Show Available only"}
                        </Button>
                        <Text size="xs" c="dimmed" mt={8} mb={4}>Available within</Text>
                        <Group gap={4} wrap="nowrap">
                            {([7, 15, 30, 60] as const).map((days) => (
                                <Button
                                    key={days}
                                    size="compact-xs"
                                    variant={availabilityDays === days ? "filled" : "light"}
                                    color="brand"
                                    style={{ flex: 1 }}
                                    onClick={() => onAvailabilityDaysChange(availabilityDays === days ? null : days)}
                                    disabled={isFiltered}
                                    aria-pressed={availabilityDays === days}
                                    aria-label={`Available within ${days} days`}
                                >
                                    {days}d
                                </Button>
                            ))}
                        </Group>
                    </Box>
                </FilterPill>

                {/* Office location */}
                <FilterPill
                    icon={
                        <IconBuilding
                            style={{
                                width: rem(14),
                                height: rem(14),
                                color: selectedLocation
                                    ? "var(--mantine-color-brand-5)"
                                    : "var(--mantine-color-dimmed)",
                            }}
                        />
                    }
                    label="Office"
                    disabled={isFiltered}
                    tags={
                        selectedLocationLabel
                            ? [{ label: selectedLocationLabel, onRemove: () => onLocationChange(null) }]
                            : []
                    }
                >
                    <Box pt={6}>
                        <Select
                            placeholder="All offices"
                            value={selectedLocation}
                            onChange={onLocationChange}
                            data={[{ value: "", label: "All office locations" }, ...locationOptions]}
                            size="xs"
                            clearable
                            searchable
                            disabled={isFiltered}
                            style={{ minWidth: rem(160) }}
                        />
                    </Box>
                </FilterPill>

                {/* Language + level */}
                <FilterPill
                    icon={
                        <IconGlobe
                            style={{
                                width: rem(14),
                                height: rem(14),
                                color: selectedLanguage
                                    ? "var(--mantine-color-brand-5)"
                                    : "var(--mantine-color-dimmed)",
                            }}
                        />
                    }
                    label="Language"
                    disabled={isFiltered}
                    tags={[
                        ...(selectedLanguageLabel
                            ? [{
                                label: selectedLanguageLabel,
                                onRemove: () => {
                                    onLanguageChange(null);
                                    onProficiencyChange(null);
                                },
                            }]
                            : []),
                        ...(selectedProficiencyLabel
                            ? [{ label: selectedProficiencyLabel, onRemove: () => onProficiencyChange(null) }]
                            : []),
                    ]}
                >
                    <Stack gap={6} pt={6}>
                        <Select
                            placeholder="Any language"
                            value={selectedLanguage?.toString() || null}
                            onChange={(value) => {
                                onLanguageChange(value ? parseInt(value) : null);
                                if (!value) onProficiencyChange(null);
                            }}
                            data={languages}
                            size="xs"
                            clearable
                            searchable
                            disabled={isFiltered}
                            style={{ minWidth: rem(160) }}
                        />
                        <Tooltip
                            label="Select a language first"
                            disabled={!!selectedLanguage}
                            withArrow
                            position="bottom"
                        >
                            <Select
                                placeholder="Any level"
                                value={selectedProficiency?.toString() || null}
                                onChange={(value) =>
                                    onProficiencyChange(value ? parseInt(value) : null)
                                }
                                data={proficiencies}
                                size="xs"
                                clearable
                                searchable
                                disabled={isFiltered || !selectedLanguage}
                                style={{ minWidth: rem(160) }}
                            />
                        </Tooltip>
                    </Stack>
                </FilterPill>

                {/* Exact match / Keywords only */}
                <FilterPill
                    icon={
                        <IconFingerPrint
                            style={{
                                width: rem(14),
                                height: rem(14),
                                color: config.exactMatchOnly || !config.useTechSearch || !config.useCertSearch
                                    ? "var(--mantine-color-brand-5)"
                                    : "var(--mantine-color-dimmed)",
                            }}
                        />
                    }
                    label="Match Mode"
                    disabled={isFiltered}
                    tags={[
                        ...(config.exactMatchOnly
                            ? [{ label: "Match Exactly", onRemove: () => updateConfig({ exactMatchOnly: false }) }]
                            : []),
                        ...(!config.useTechSearch
                            ? [{ label: "Tech off", onRemove: () => updateConfig({ useTechSearch: true }) }]
                            : []),
                        ...(!config.useCertSearch
                            ? [{ label: "Cert off", onRemove: () => updateConfig({ useCertSearch: true }) }]
                            : []),
                    ]}
                >
                    <Box pt={6}>
                        <Button
                            fullWidth
                            size="compact-xs"
                            variant={config.exactMatchOnly ? "filled" : "light"}
                            color="brand"
                            onClick={() => updateConfig({
                                exactMatchOnly: !config.exactMatchOnly,
                            })}
                            disabled={isFiltered}
                            aria-pressed={config.exactMatchOnly}
                            mb={4}
                        >
                            {config.exactMatchOnly ? "✓ Match Exactly" : "Match Exactly"}
                        </Button>
                        <Button
                            fullWidth
                            size="compact-xs"
                            variant={config.useTechSearch ? "filled" : "light"}
                            color="brand"
                            onClick={() => updateConfig({
                                useTechSearch: !config.useTechSearch,
                            })}
                            disabled={isFiltered}
                            aria-pressed={config.useTechSearch}
                            mb={4}
                        >
                            {config.useTechSearch ? "✓ Use Technologies" : "Use Technologies"}
                        </Button>
                        <Button
                            fullWidth
                            size="compact-xs"
                            variant={config.useCertSearch ? "filled" : "light"}
                            color="brand"
                            onClick={() => updateConfig({
                                useCertSearch: !config.useCertSearch,
                            })}
                            disabled={isFiltered}
                            aria-pressed={config.useCertSearch}
                        >
                            {config.useCertSearch ? "✓ Use Certificates" : "Use Certificates"}
                        </Button>
                    </Box>
                </FilterPill>

                {/* Show inactive */}
                <FilterPill
                    icon={
                        <IconUser
                            style={{
                                width: rem(14),
                                height: rem(14),
                                color: inactiveFilterActive
                                    ? "var(--mantine-color-brand-5)"
                                    : "var(--mantine-color-dimmed)",
                            }}
                        />
                    }
                    label="Show Inactive"
                    disabled={isFiltered}
                    tags={
                        inactiveFilterActive
                            ? [{ label: "Inactive", onRemove: onInactiveFilterChange }]
                            : []
                    }
                >
                    <Box pt={6}>
                        <Button
                            fullWidth
                            size="compact-xs"
                            variant={inactiveFilterActive ? "filled" : "light"}
                            color="brand"
                            onClick={onInactiveFilterChange}
                            disabled={isFiltered}
                            aria-pressed={inactiveFilterActive}
                        >
                            {inactiveFilterActive ? "✓ Including inactive" : "Include inactive"}
                        </Button>
                    </Box>
                </FilterPill>

            </Group>
        </Stack>
    );
}

